import React from 'react';
import Grid from "@mui/material/Grid";
import { Table, TableBody, TableRow, TableCell, TableHead, TableContainer, Typography, CircularProgress, IconButton, Collapse, Box } from "@mui/material";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SelectFarmWarehouse from '../../../components/SelectFarmWarehouse';
import JumboCardQuick from "@jumbo/components/JumboCardQuick";
import { getAPIDataParamsReturn } from "../../../utils/apiFunction";
import { dateFormat } from "../../../utils/dateFunctions";
import { renderStatusArrow } from "../../../utils/flipStatusUtils";

const FlipHistory = (props) => {
    const [farm, setFarm] = React.useState(null);

    return (
        <React.Fragment>
            <Grid container spacing={2} mb={1}>
                <Grid item xs={12} sm={12} md={6} lg={4} xl={3}>
                    <SelectFarmWarehouse setState={setFarm} state={farm} access={props.access} />
                </Grid>
                <Grid item xs={12}>
                    <FlipHistoryContent warehouseId={farm?.warehouseId} />
                </Grid>
            </Grid>
        </React.Fragment>
    );
};

export default FlipHistory;

const FlipHistoryContent = (props) => {
    const [loading, setLoading] = React.useState(false);
    const [events, setEvents] = React.useState([]);
    const [openLeases, setOpenLeases] = React.useState({});
    const [openSectionHistory, setOpenSectionHistory] = React.useState({});

    React.useEffect(() => {
        const abortController = new AbortController();
        setEvents([]);
        if (props.warehouseId) {
            loadData(props.warehouseId, abortController);
        }
        return () => {
            abortController.abort();
        };
    }, [props.warehouseId]);

    const loadData = async (warehouseId, abortController) => {
        setLoading(true);
        const data = await getAPIDataParamsReturn('/Farms/Groups/FlipHistory', { WarehouseId: warehouseId }, abortController);
        const sorted = (data || []).map(e => ({
            ...e,
            rawDate: e.date
        })).sort((a, b) => new Date(b.rawDate) - new Date(a.rawDate));
        setEvents(sorted);
        setLoading(false);
    };

    const groupByLastFlipByLease = () => {
        const byGroup = {};
        events.forEach(e => {
            if (e.groupId === null || e.groupId === undefined) {
                return;
            }
            if (!byGroup[e.groupId]) {
                byGroup[e.groupId] = [];
            }
            byGroup[e.groupId].push(e);
        });

        // Build a full flip history signature per group so that
        // sections only contain groups that share the same history.
        const groupsWithHistory = Object.entries(byGroup).map(([groupId, list]) => {
            const ordered = [...list].sort((a, b) => new Date(a.rawDate) - new Date(b.rawDate));
            const historySignature = ordered
                .map(ev => {
                    const d = new Date(ev.rawDate);
                    const day = d.toISOString().substring(0, 10);
                    return `${day}_${ev.status}`;
                })
                .join('|');
            const last = ordered[ordered.length - 1];
            return {
                groupId: Number(groupId),
                historySignature,
                lastDate: last.rawDate,
                lastStatus: last.status,
                sample: last,
            };
        });

        const byLease = {};
        groupsWithHistory.forEach(g => {
            const leaseId = g.sample.leaseId;
            if (!byLease[leaseId]) {
                byLease[leaseId] = {
                    leaseId: leaseId,
                    leaseName: g.sample.leaseName,
                    leaseNo: g.sample.leaseNo,
                    sections: {}
                };
            }
            const key = g.historySignature;
            if (!byLease[leaseId].sections[key]) {
                byLease[leaseId].sections[key] = {
                    historySignature: g.historySignature,
                    lastDate: g.lastDate,
                    lastStatus: g.lastStatus,
                    rows: []
                };
            }
            byLease[leaseId].sections[key].rows.push(g.sample);
        });

        return Object.values(byLease).map(l => ({
            ...l,
            sections: Object.values(l.sections).sort((a, b) => new Date(b.lastDate) - new Date(a.lastDate))
        })).sort((a, b) => (a.leaseName || '').localeCompare(b.leaseName || ''));
    };

    const leases = groupByLastFlipByLease();

    return (
            <FlipHistoryByLastStatusTable
                loading={loading}
                leases={leases}
                events={events}
                openLeases={openLeases}
                setOpenLeases={setOpenLeases}
                openSectionHistory={openSectionHistory}
                setOpenSectionHistory={setOpenSectionHistory}
            />
           
    );
};

const FlipHistoryByLastStatusTable = ({
    loading,
    leases,
    events,
    openLeases,
    setOpenLeases,
    openSectionHistory,
    setOpenSectionHistory,
}) => (
    <JumboCardQuick
        title={<span>Flip History</span>}
        wrapperSx={{
            p: 0,
            backgroundColor: 'background.paper',
            zIndex: 0,
            '&:last-child': {
                pb: 0,
            },
        }}
    >
        {loading ? (
            <CircularProgress />
        ) : (
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell />
                            <TableCell>Lease</TableCell>
                            <TableCell>Lease No</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {leases.map((lease, li) => {
                            const isOpen = openLeases[lease.leaseId] || false;
                            
                            return (
                                <LeaseTableSection
                                    key={lease.leaseId || li}
                                    lease={lease}
                                    events={events}
                                    isOpen={isOpen}
                                    onToggle={() =>
                                        setOpenLeases(prev => ({
                                            ...prev,
                                            [lease.leaseId]: !isOpen,
                                        }))
                                    }
                                    openSectionHistory={openSectionHistory}
                                    setOpenSectionHistory={setOpenSectionHistory}
                                />
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        )}
    </JumboCardQuick>
);

const LeaseTableSection = ({
    lease,
    events,
    isOpen,
    onToggle,
    openSectionHistory,
    setOpenSectionHistory,
}) => (
    <React.Fragment>
        <TableRow>
            <TableCell>
                <IconButton
                    aria-label="expand row"
                    size="small"
                    onClick={onToggle}
                >
                    {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </IconButton>
            </TableCell>
            <TableCell>{lease.leaseName}</TableCell>
            <TableCell>{lease.leaseNo}</TableCell>
        </TableRow>
        <TableRow>
            <TableCell
                style={{
                    paddingBottom: 0,
                    paddingTop: 0,
                }}
                colSpan={4}
            >
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <Box sx={{ margin: 1 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell />
                                    <TableCell>Batch</TableCell>
                                    <TableCell>Grade</TableCell>
                                    <TableCell>Unit Type</TableCell>
                                    <TableCell>Rows</TableCell>
                                    <TableCell align="right">Units</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {lease.sections.map((sec, si) => (
                                    <LeaseSectionRows
                                        key={`${lease.leaseId}-sec-${si}`}
                                        lease={lease}
                                        section={sec}
                                        sectionIndex={si}
                                        events={events}
                                        openSectionHistory={openSectionHistory}
                                        setOpenSectionHistory={setOpenSectionHistory}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                </Collapse>
            </TableCell>
        </TableRow>
    </React.Fragment>
);

const LeaseSectionRows = ({
    lease,
    section,
    sectionIndex,
    events,
    openSectionHistory,
    setOpenSectionHistory,
}) => {
    const groupTotals = [];
    section.rows.forEach(r => {
        const key = `${r.cropLabel}|${r.grade}|${r.unitType}`;
        let gt = groupTotals.find(g => g.key === key);
        if (!gt) {
            gt = {
                key,
                cropLabel: r.cropLabel,
                grade: r.grade,
                unitType: r.unitType,
                rows: new Set(),
                totalUnits: 0,
                groupIds: new Set(),
            };
            groupTotals.push(gt);
        }
        
        if (r.groupId !== null && r.groupId !== undefined) {
            gt.groupIds.add(r.groupId);
        }
        if (r.lineNbr !== null && r.lineNbr !== undefined) {
            gt.rows.add(r.lineNbr);
        }
        gt.totalUnits += r.totalUnits || 0;
    });

    const sectionKey = `${lease.leaseId}-sec-${sectionIndex}`;
    const isSectionOpen = openSectionHistory[sectionKey] || false;
    const sectionGroupIds = new Set();
    groupTotals.forEach(gt => {
        gt.groupIds.forEach(id => sectionGroupIds.add(id));
    });

    const sectionHistoryRows = events
        .filter(e => sectionGroupIds.has(e.groupId) && e.leaseId === lease.leaseId)
        .sort((a, b) => new Date(a.rawDate) - new Date(b.rawDate));

    const historyMap = {};
    const aggregatedHistory = [];
    sectionHistoryRows.forEach(hr => {
        const d = new Date(hr.rawDate);
        const day = d.toISOString().substring(0, 10);
        const keyAgg = `${day}_${hr.status}`;
        if (!historyMap[keyAgg]) {
            historyMap[keyAgg] = {
                rawDate: hr.rawDate,
                status: hr.status,
                units: 0,
            };
            aggregatedHistory.push(historyMap[keyAgg]);
        }
        historyMap[keyAgg].units += hr.totalUnits || 0;
    });

    aggregatedHistory.sort((a, b) => new Date(b.rawDate)- new Date(a.rawDate));

    return (
        <React.Fragment>
            <TableRow>
                <TableCell colSpan={6}>
                    <Typography variant="subtitle1">
                        {dateFormat(new Date(section.lastDate))}{' '}
                         {renderStatusArrow(section.lastStatus)}
                    </Typography>
                </TableCell>
            </TableRow>
            {groupTotals.map((gt, gi) => (
                <TableRow key={`${lease.leaseId}-sec-${sectionIndex}-grp-${gi}`}>
                    <TableCell>
                        {aggregatedHistory.length > 0 && (
                            <IconButton
                                aria-label="expand history"
                                size="small"
                                onClick={() =>
                                    setOpenSectionHistory(prev => ({
                                        ...prev,
                                        [sectionKey]: !isSectionOpen,
                                    }))
                                }
                            >
                                {isSectionOpen ? (
                                    <KeyboardArrowUpIcon />
                                ) : (
                                    <KeyboardArrowDownIcon />
                                )}
                            </IconButton>
                        )}
                    </TableCell>
                    <TableCell>
                            {gt.cropLabel}
                    </TableCell>
                    <TableCell>{gt.grade}</TableCell>
                    <TableCell>{gt.unitType}</TableCell>
                    <TableCell>
                        {Array.from(gt.rows)
                            .sort((a, b) => a - b)
                            .join(', ')}
                    </TableCell>
                    <TableCell align="right">{gt.totalUnits}</TableCell>
                </TableRow>
            ))}
            {aggregatedHistory.length > 0 && (
                <TableRow>
                    <TableCell
                        style={{
                            paddingBottom: 0,
                            paddingTop: 0,
                        }}
                        colSpan={2}
                    >
                        <Collapse in={isSectionOpen} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 1 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Status</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {aggregatedHistory.map((hr, hi) => (
                                            <TableRow
                                                key={`${sectionKey}-hist-${hi}`}
                                            >
                                                <TableCell>
                                                    {dateFormat(
                                                        new Date(hr.rawDate),
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {renderStatusArrow(
                                                        hr.status,
                                                    )}
                                                </TableCell>
                                                
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        </Collapse>
                    </TableCell>
                </TableRow>
            )}
        </React.Fragment>
    );
};
